import { state, getActiveSequence, keyframeToText, makeId, pushUndo, notify, migrateProject } from './state.js';
import { showToast, closeModal } from './ui.js';
import { renderAll } from './render-bridge.js';

// ================================================================
// IMPORT / EXPORT
// ================================================================
export function setupImportExport() {
  document.getElementById("btn-import").addEventListener("click", showImportModal);
  document.getElementById("btn-export-json").addEventListener("click", exportFullJSON);
}

export function exportFullJSON() {
  const json = JSON.stringify(state.project, null, 2);
  const blob = new Blob([json], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "animation.json";
  a.click();
  URL.revokeObjectURL(url);
  showToast("Exported JSON");
}

// ----------------------------------------------------------------
// Export current sequence as a standalone JSON (with its keyframes)
// ----------------------------------------------------------------
export function exportSequenceJSON() {
  const seq = getActiveSequence();
  if (!seq) return;
  const usedKfIds = new Set(seq.steps.map(s => s.keyframeId));
  const keyframes = {};
  for (const id of usedKfIds) {
    if (state.project.keyframes[id]) keyframes[id] = state.project.keyframes[id];
  }
  const data = { version: 1, sequence: seq, keyframes };
  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = (seq.name || "timeline").replace(/[^a-zA-Z0-9_-]/g, "-") + ".json";
  a.click();
  URL.revokeObjectURL(url);
  showToast("Exported: " + (seq.name || "timeline"));
}

// ----------------------------------------------------------------
// Import a timeline JSON (merges keyframes, adds as new sequence)
// ----------------------------------------------------------------
export function importSequenceFromJSON() {
  const modal = document.getElementById("modal");
  modal.innerHTML = `
    <h3>Import Timeline from JSON</h3>
    <p style="font-size:11px;color:var(--text);margin-bottom:8px;">Paste timeline JSON exported from this app:</p>
    <textarea id="import-seq-textarea" placeholder='{"version":1, "sequence":{...}, "keyframes":{...}}'></textarea>
    <div class="modal-actions">
      <button class="btn-cancel" id="import-seq-cancel">Cancel</button>
      <button class="btn-primary" id="import-seq-confirm">Import</button>
    </div>
  `;
  document.getElementById("modal-overlay").classList.add("open");
  document.getElementById("import-seq-cancel").addEventListener("click", closeModal);
  document.getElementById("import-seq-confirm").addEventListener("click", () => {
    const text = document.getElementById("import-seq-textarea").value.trim();
    try {
      const data = JSON.parse(text);
      if (!data.sequence || !data.keyframes || !data.version) throw new Error("Invalid timeline format");
      pushUndo();
      const idMap = {};
      for (const [oldId, kf] of Object.entries(data.keyframes)) {
        const newId = makeId();
        idMap[oldId] = newId;
        state.project.keyframes[newId] = { ...kf, id: newId };
      }
      const newSeq = { ...data.sequence };
      newSeq.steps = data.sequence.steps.map(s => ({
        ...s,
        keyframeId: idMap[s.keyframeId] || s.keyframeId,
      }));
      state.project.sequences.push(newSeq);
      state.ui.activeSequenceIdx = state.project.sequences.length - 1;
      state.ui.playbackStep = 0;
      closeModal();
      notify("importSequence");
      renderAll();
      showToast("Imported: " + (newSeq.name || "timeline"));
    } catch(e) {
      showToast("Import failed: " + e.message);
    }
  });
}

// ----------------------------------------------------------------
// Export active sequence as standalone HTML (single animation)
// ----------------------------------------------------------------
export function exportHTML() {
  const seq = getActiveSequence();
  if (!seq || seq.steps.length === 0) {
    showToast("No animation steps to export");
    return;
  }

  const { posesJS, seqJS } = buildAnimationJS(seq);

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>Unicode Animation</title>
<style>
  body {
    background: ${state.project.backgroundColor};
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100vh;
    margin: 0;
    font-family: "Menlo", "Monaco", "Consolas", "Courier New", monospace;
  }
  #display {
    font-size: ${state.project.fontSize}px;
    line-height: 1.0;
    white-space: pre;
    letter-spacing: 0px;
    user-select: none;
  }
</style>
</head>
<body>
<div id="display"></div>
<script>
${posesJS}

${seqJS}

function render(poseName) {
  const p = poses[poseName];
  const el = document.getElementById("display");
  el.style.backgroundColor = p.background || "";
  document.body.style.background = p.background || ${JSON.stringify(state.project.backgroundColor || "")};
  if (p.colorMap && p.colorMap.length > 0) {
    el.innerHTML = "";
    const lines = p.text.split("\\n");
    for (let r = 0; r < lines.length; r++) {
      const chars = [...lines[r]];
      for (let c = 0; c < chars.length; c++) {
        const cm = p.colorMap.find(m => m.r === r && m.c === c);
        const span = document.createElement("span");
        span.style.color = cm ? cm.color : p.color;
        span.textContent = chars[c];
        el.appendChild(span);
      }
      if (r < lines.length - 1) el.appendChild(document.createTextNode("\\n"));
    }
  } else {
    el.style.color = p.color;
    el.textContent = p.text;
  }
}

let step = 0;
function animate() {
  const { pose, duration } = sequence[step % sequence.length];
  render(pose);
  step++;
  setTimeout(animate, duration);
}
animate();
<\/script>
</body>
</html>`;

  downloadBlob(html, "text/html", (seq.name || "animation").replace(/[^a-zA-Z0-9_-]/g, "-") + ".html");
  showToast("Exported HTML");
}

// ----------------------------------------------------------------
// Export ALL sequences in one HTML file with a selector UI
// ----------------------------------------------------------------
export function exportAllHTML() {
  const validSeqs = state.project.sequences.filter(s => s.steps.length > 0);
  if (validSeqs.length === 0) {
    showToast("No animation steps to export");
    return;
  }

  let animationsJS = "const animations = {\n";
  const animKeys = [];

  for (let si = 0; si < validSeqs.length; si++) {
    const seq = validSeqs[si];
    const animKey = "anim_" + si;
    const animName = (seq.name || ("Timeline " + (si + 1))).replace(/"/g, '\\"');
    animKeys.push({ key: animKey, name: seq.name || ("Timeline " + (si + 1)) });

    const { posesObj, seqArr } = buildAnimationData(seq);

    animationsJS += `  "${animKey}": {\n`;
    animationsJS += `    name: "${animName}",\n`;
    animationsJS += `    poses: ${JSON.stringify(posesObj, null, 4).replace(/^/gm, "    ").trimStart()},\n`;
    animationsJS += `    sequence: ${JSON.stringify(seqArr, null, 4).replace(/^/gm, "    ").trimStart()},\n`;
    animationsJS += `  },\n`;
  }
  animationsJS += "};";

  const selectorBtns = animKeys.map(a =>
    `  <button data-key="${a.key}" onclick="switchAnim('${a.key}')">${a.name}</button>`
  ).join("\n");

  const firstKey = animKeys[0].key;

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>Unicode Animations</title>
<style>
  body {
    background: ${state.project.backgroundColor};
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100vh;
    margin: 0;
    font-family: "Menlo", "Monaco", "Consolas", "Courier New", monospace;
    gap: 20px;
  }
  #selector { display: flex; gap: 8px; flex-wrap: wrap; justify-content: center; }
  #selector button {
    padding: 6px 16px;
    background: transparent;
    border: 1px solid #1a5a6a;
    color: #93a1a1;
    font-family: inherit;
    font-size: 12px;
    cursor: pointer;
    border-radius: 4px;
    transition: all 0.15s;
  }
  #selector button.active { border-color: #ca4b15; color: #eee8d5; }
  #display {
    font-size: ${state.project.fontSize}px;
    line-height: 1.0;
    white-space: pre;
    letter-spacing: 0px;
    user-select: none;
  }
</style>
</head>
<body>
<div id="selector">
${selectorBtns}
</div>
<div id="display"></div>
<script>
${animationsJS}

let currentAnim = null;
let step = 0;
let timer = null;

function render(poseName) {
  const p = currentAnim.poses[poseName];
  const el = document.getElementById("display");
  el.style.backgroundColor = p.background || "";
  document.body.style.background = p.background || ${JSON.stringify(state.project.backgroundColor || "")};
  if (p.colorMap && p.colorMap.length > 0) {
    el.innerHTML = "";
    const lines = p.text.split("\\n");
    for (let r = 0; r < lines.length; r++) {
      const chars = [...lines[r]];
      for (let c = 0; c < chars.length; c++) {
        const cm = p.colorMap.find(m => m.r === r && m.c === c);
        const span = document.createElement("span");
        span.style.color = cm ? cm.color : p.color;
        span.textContent = chars[c];
        el.appendChild(span);
      }
      if (r < lines.length - 1) el.appendChild(document.createTextNode("\\n"));
    }
  } else {
    el.style.color = p.color;
    el.textContent = p.text;
  }
}

function animate() {
  if (!currentAnim || !currentAnim.sequence.length) return;
  const { pose, duration } = currentAnim.sequence[step % currentAnim.sequence.length];
  render(pose);
  step++;
  timer = setTimeout(animate, duration);
}

function switchAnim(key) {
  clearTimeout(timer);
  currentAnim = animations[key];
  step = 0;
  document.querySelectorAll("#selector button").forEach(b => {
    b.classList.toggle("active", b.dataset.key === key);
  });
  animate();
}

switchAnim("${firstKey}");
<\/script>
</body>
</html>`;

  downloadBlob(html, "text/html", "animations.html");
  showToast("Exported All HTML (" + validSeqs.length + " timelines)");
}

// ----------------------------------------------------------------
// Shared helpers for building animation JS data
// ----------------------------------------------------------------
export function buildAnimationData(seq) {
  const usedKfIds = new Set(seq.steps.map(s => s.keyframeId));
  const nameMap = {};
  for (const id of usedKfIds) {
    const kf = state.project.keyframes[id];
    if (kf) nameMap[id] = kf.name.replace(/[^a-zA-Z0-9_-]/g, "-");
  }

  const posesObj = {};
  for (const id of usedKfIds) {
    const kf = state.project.keyframes[id];
    if (!kf) continue;
    const text = keyframeToText(kf);
    const colorMap = [];
    for (let r = 0; r < kf.rows; r++) {
      for (let c = 0; c < kf.cols; c++) {
        if (kf.grid[r][c].color) colorMap.push({ r, c, color: kf.grid[r][c].color });
      }
    }
    const entry = { text, color: kf.defaultColor };
    if (colorMap.length > 0) entry.colorMap = colorMap;
    if (kf.backgroundColor) entry.background = kf.backgroundColor;
    posesObj[nameMap[id]] = entry;
  }

  const seqArr = seq.steps.map(s => ({
    pose: nameMap[s.keyframeId] || "unknown",
    duration: s.duration,
  }));

  return { posesObj, seqArr, nameMap };
}

function buildAnimationJS(seq) {
  const { posesObj, seqArr } = buildAnimationData(seq);

  let posesJS = "const poses = {\n";
  for (const [name, p] of Object.entries(posesObj)) {
    const text = p.text.replace(/\\/g, "\\\\").replace(/`/g, "\\`");
    posesJS += `  "${name}": { text: \`${text}\`, color: "${p.color}"`;
    if (p.colorMap) posesJS += `, colorMap: ${JSON.stringify(p.colorMap)}`;
    if (p.background) posesJS += `, background: "${p.background}"`;
    posesJS += ` },\n`;
  }
  posesJS += "};";

  let seqJS = "const sequence = [\n";
  for (const s of seqArr) {
    seqJS += `  { pose: "${s.pose}", duration: ${s.duration} },\n`;
  }
  seqJS += "];";

  return { posesJS, seqJS };
}

function downloadBlob(content, type, filename) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function copyJS() {
  const seq = getActiveSequence();
  if (!seq || seq.steps.length === 0) {
    showToast("No animation to copy");
    return;
  }

  const { posesObj, seqArr } = buildAnimationData(seq);

  let js = "const poses = {\n";
  for (const [name, p] of Object.entries(posesObj)) {
    const text = p.text.replace(/\\/g, "\\\\").replace(/"/g, '\\"').replace(/\n/g, "\\n");
    js += `  "${name}": "${text}",\n`;
  }
  js += "};\n\nconst sequence = [\n";
  for (const s of seqArr) {
    js += `  { pose: "${s.pose}", duration: ${s.duration} },\n`;
  }
  js += "];";

  navigator.clipboard.writeText(js).then(() => showToast("Copied JS to clipboard"));
}

function showImportModal() {
  const modal = document.getElementById("modal");
  modal.innerHTML = `
    <h3>Import Project</h3>
    <p style="font-size:11px;color:var(--text);margin-bottom:8px;">Paste JSON project data:</p>
    <textarea id="import-textarea" placeholder='{"version":1, ...}'></textarea>
    <div class="modal-actions">
      <button class="btn-cancel" id="import-cancel">Cancel</button>
      <button class="btn-primary" id="import-confirm">Import</button>
    </div>
  `;
  document.getElementById("modal-overlay").classList.add("open");
  document.getElementById("import-cancel").addEventListener("click", closeModal);
  document.getElementById("import-confirm").addEventListener("click", () => {
    const text = document.getElementById("import-textarea").value.trim();
    try {
      const proj = JSON.parse(text);
      if (!proj.keyframes || !proj.version) throw new Error("Invalid format");
      pushUndo();
      state.project = migrateProject(proj);
      if (!state.project.sequences) state.project.sequences = [{ name: "animation", steps: [], loop: true }];
      const ids = Object.keys(state.project.keyframes);
      state.ui.selectedKeyframeId = ids[0] || null;
      state.ui.selectedCell = null;
      state.ui.activeSequenceIdx = 0;
      closeModal();
      renderAll();
      showToast("Imported successfully");
    } catch(e) {
      showToast("Import failed: " + e.message);
    }
  });
}

